'use client';

import {
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { JobTemplate } from '@repo/api-gw-sdk';
import { useEffect, useRef, useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { useDAL } from '@/contexts/useDAL';

interface EditJobTemplateDialogProps {
  template: JobTemplate;
  workspaceId: string;
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

export const EditJobTemplateDialog = ({
  template,
  workspaceId,
  open,
  onClose,
  onUpdated,
}: EditJobTemplateDialogProps) => {
  const dal = useDAL();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [entryPoint, setEntryPoint] = useState('');
  const [entryPointArguments, setEntryPointArguments] = useState('');
  const [sparkSubmitParameters, setSparkSubmitParameters] = useState('');
  const [jobTimeout, setJobTimeout] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prevOpenRef = useRef(false);

  // Initialize form with template values only when dialog transitions from closed to open
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setName(template.name);
      setDescription(template.description ?? '');
      setEntryPoint(template.sparkSubmitParams?.entryPoint ?? '');
      setEntryPointArguments(
        template.sparkSubmitParams?.entryPointArguments?.join(' ') ?? ''
      );
      setSparkSubmitParameters(
        template.sparkSubmitParams?.sparkSubmitParameters ?? ''
      );
      setJobTimeout(template.jobTimeout?.toString() ?? '');
      setError(null);
    }
    prevOpenRef.current = open;
  }, [open, template]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleUpdate = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!entryPoint.trim()) {
      setError('Entry point is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const args = entryPointArguments
        .trim()
        .split(/\s+/)
        .filter((arg) => arg.length > 0);

      await dal.lakehouse.updateJobTemplate(workspaceId, template.id, {
        name: name.trim(),
        description: description.trim() || null,
        jobTimeout: jobTimeout ? parseInt(jobTimeout, 10) : null,
        sparkSubmitParams: {
          entryPoint: entryPoint.trim(),
          entryPointArguments: args.length > 0 ? args : null,
          sparkSubmitParameters: sparkSubmitParameters.trim() || null,
        },
      });
      onUpdated();
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update job template'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainDialog
      isOpen={open}
      isLoading={isLoading}
      header={{
        title: 'Edit Job Template',
        onClose: handleClose,
      }}
      footer={{
        primaryButton: {
          text: 'Save Changes',
          onClick: handleUpdate,
          'data-testid': 'update-job-template-btn',
          disabled: isLoading || !name.trim() || !entryPoint.trim(),
        },
        secondaryButton: {
          text: 'Cancel',
          onClick: handleClose,
          'data-testid': 'cancel-update-job-template-btn',
        },
      }}
    >
      <Stack gap='20px' padding='0 40px'>
        <FormControl fullWidth>
          <FormLabel>
            <Typography variant='subtitle1'>Name *</Typography>
          </FormLabel>
          <TextField
            placeholder='Enter template name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!error && !name.trim()}
            fullWidth
            size='small'
          />
        </FormControl>

        <FormControl fullWidth>
          <FormLabel>
            <Typography variant='subtitle1'>Description</Typography>
          </FormLabel>
          <TextField
            placeholder='Enter description (optional)'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            size='small'
            multiline
            rows={2}
          />
        </FormControl>

        <FormControl fullWidth>
          <FormLabel>
            <Typography variant='subtitle1'>Entry Point *</Typography>
          </FormLabel>
          <TextField
            placeholder='s3://bucket/path/to/application.jar'
            value={entryPoint}
            onChange={(e) => setEntryPoint(e.target.value)}
            error={!!error && !entryPoint.trim()}
            fullWidth
            size='small'
          />
          <FormHelperText>
            S3 path to your Spark application JAR or Python script
          </FormHelperText>
        </FormControl>

        <FormControl fullWidth>
          <FormLabel>
            <Typography variant='subtitle1'>Entry Point Arguments</Typography>
          </FormLabel>
          <TextField
            placeholder='arg1 arg2 arg3'
            value={entryPointArguments}
            onChange={(e) => setEntryPointArguments(e.target.value)}
            fullWidth
            size='small'
          />
          <FormHelperText>
            Space-separated arguments to pass to the entry point
          </FormHelperText>
        </FormControl>

        <FormControl fullWidth>
          <FormLabel>
            <Typography variant='subtitle1'>Spark Submit Parameters</Typography>
          </FormLabel>
          <TextField
            placeholder='--conf spark.executor.memory=4g'
            value={sparkSubmitParameters}
            onChange={(e) => setSparkSubmitParameters(e.target.value)}
            fullWidth
            size='small'
            multiline
            rows={2}
          />
          <FormHelperText>Additional Spark submit parameters</FormHelperText>
        </FormControl>

        <FormControl fullWidth>
          <FormLabel>
            <Typography variant='subtitle1'>Job Timeout (seconds)</Typography>
          </FormLabel>
          <TextField
            placeholder='3600'
            value={jobTimeout}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '' || /^\d+$/.test(val)) {
                setJobTimeout(val);
              }
            }}
            fullWidth
            size='small'
            type='number'
            inputProps={{ min: 1 }}
          />
          <FormHelperText>
            Maximum time the job is allowed to run (optional)
          </FormHelperText>
        </FormControl>

        {error && (
          <Typography color='error' variant='body2'>
            {error}
          </Typography>
        )}
      </Stack>
    </MainDialog>
  );
};
